// File: src/components/InputForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import ResultCard from './ResultCard';
import FactCheckService from '../services/FactCheckService';
import { getErrorMessage, isLimitError } from '../utils/errorHandler';
import './InputForm.css';

function InputForm({ onQueryComplete, onLimitReached, initialResult }) {

  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [inputType, setInputType] = useState('link'); // 'link', 'text', 'image'
  const [inputValue, setInputValue] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(null); // { pct, stage } while streaming

  // Sync with initialResult (History)
  React.useEffect(() => {
    if (initialResult) {
      setResult(initialResult);
      if (initialResult.type) setInputType(initialResult.type);
      if (initialResult.text && initialResult.type !== 'image') {
        setInputValue(initialResult.text);
      } else {
        setInputValue('');
      }
      // Clear image preview when loading from history (since we don't have the local file)
      setImagePreview(null);
      setSelectedImage(null);

      // Scroll to result
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [initialResult]);

  const processFile = (file) => {
    if (file) {
      // File size validation (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setError(`File size exceeds maximum allowed size of 10MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
        setSelectedImage(null);
        setImagePreview(null);
        return;
      }

      // Strict validation for supported formats
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/mpo'];
      if (!validTypes.includes(file.type)) {
        setError("Unsupported file format. Please use PNG, JPEG, GIF, WEBP, or MPO.");
        setSelectedImage(null);
        setImagePreview(null);
        return;
      }

      setError(null);
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setResult(null);

    let contentToAnalyze = '';

    if (inputType === 'link') {
      const trimmedUrl = inputValue.trim();
      if (!trimmedUrl) {
        setError("Please enter a URL");
        return;
      }
      if (!FactCheckService.isValidUrl(trimmedUrl)) {
        setError("Please enter a valid URL format");
        return;
      }
      contentToAnalyze = trimmedUrl;
    } else if (inputType === 'text') {
      const trimmedText = inputValue.trim();
      if (!trimmedText) {
        setError("Please enter some text");
        return;
      }
      contentToAnalyze = trimmedText;
    } else if (inputType === 'image') {
      if (!selectedImage) {
        setError("Please select an image");
        return;
      }
      try {
        contentToAnalyze = await convertToBase64(selectedImage);
      } catch (err) {
        setError("Failed to process image");
        return;
      }
    }

    setLoading(true);
    setProgress({ pct: 0, stage: 'Starting…' });

    try {
      const token = await getToken();
      let data;
      try {
        // Stream live progress; the bar advances as the backend reports stages.
        data = await FactCheckService.analyzeStream(
          contentToAnalyze, inputType, token,
          (p) => setProgress({ pct: p.pct, stage: p.stage })
        );
      } catch (streamErr) {
        const st = streamErr?.response?.status;
        // Real auth/plan/quota gates -> let the outer handler route them.
        if (st === 401 || st === 402 || st === 429) throw streamErr;
        // Streaming endpoint unavailable (404/405 — e.g. not deployed) or a
        // transport/SSE failure -> fall back to the plain, non-streaming request.
        console.warn('Streaming unavailable, falling back to /factcheck:', streamErr.message);
        setProgress(null);
        data = await FactCheckService.analyze(contentToAnalyze, inputType, token);
      }

      // Processing-level errors still return 200 with an { error } body
      if (data.error) {
        const errorMsg = getErrorMessage({ response: { data } });
        setError(errorMsg);

        if (isLimitError({ response: { data } })) {
          if (onLimitReached) onLimitReached();
        }
        return;
      }

      // Successful response
      setResult({ ...data, input_type: inputType });
      if (onQueryComplete) onQueryComplete();

    } catch (err) {
      // Auth / subscription / quota gates come back as HTTP status codes now.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 402) {
        // No active subscription -> send to the pricing page.
        navigate('/verified');
        return;
      }
      if (status === 401) {
        // Session invalid/expired -> back to login.
        navigate('/login');
        return;
      }
      if (status === 429) {
        // Quota exhausted for the current period -> show the limit-reached wall.
        if (onLimitReached) {
          onLimitReached();
          return;
        }
        setError((detail && detail.message) || 'You have reached your query limit for this period.');
        return;
      }

      setError(getErrorMessage(err));
      if (isLimitError(err)) {
        if (onLimitReached) onLimitReached();
      }
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="input-form-container">
      <div className="input-tabs">
        <button
          className={`tab-btn ${inputType === 'link' ? 'active' : ''}`}
          onClick={() => { setInputType('link'); setError(null); }}
        >
          Link
        </button>
        <button
          className={`tab-btn ${inputType === 'text' ? 'active' : ''}`}
          onClick={() => { setInputType('text'); setError(null); }}
        >
          Text
        </button>
        <button
          className={`tab-btn ${inputType === 'image' ? 'active' : ''}`}
          onClick={() => { setInputType('image'); setError(null); }}
        >
          Image
        </button>
      </div>

      <div className="input-form-wrapper">
        {inputType === 'link' && (
          <input
            type="text"
            placeholder="Enter news article URL..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            className={`form-input ${error ? 'form-input--error' : ''}`}
          />
        )}

        {inputType === 'text' && (
          <textarea
            placeholder="Enter text to verify..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            className={`form-input form-textarea ${error ? 'form-input--error' : ''}`}
            rows={4}
          />
        )}

        {inputType === 'image' && (
          <div
            className={`image-upload-container ${isDragging ? 'dragging' : ''}`}
            onClick={() => document.getElementById('file-upload').click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
              onChange={handleFileChange}
              disabled={loading}
              className="file-input"
              id="file-upload"
            />
            <div className="upload-icon">📤</div>
            <p className="upload-hint">Drag & drop or click to select</p>
            <label htmlFor="file-upload" className="file-upload-label" onClick={(e) => e.stopPropagation()}>
              {selectedImage ? selectedImage.name : "Choose an image..."}
            </label>
            {imagePreview && (
              <div className="image-preview" onClick={(e) => e.stopPropagation()}>
                <img src={imagePreview} alt="Preview" />
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="verify-progress" role="status" aria-live="polite">
            <div className="vp-top">
              <span className="vp-stage"><span className="vp-spin" />{progress?.stage || 'Working…'}</span>
              <span className="vp-pct">{Math.round(progress?.pct || 0)}%</span>
            </div>
            <div className="vp-track">
              <div className="vp-fill" style={{ width: `${Math.max(3, Math.min(100, progress?.pct || 0))}%` }} />
            </div>
          </div>
        ) : (
          <button onClick={handleSubmit} className="form-button">
            Verify
          </button>
        )}
      </div>

      {error && (
        <div className="form-error">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      {result && <ResultCard data={result} />}
    </div>
  );
}

export default InputForm;